import React, { useMemo, useState } from 'react';
import { Input } from '@schibsted-svp/react-ui';

import { CenteredSpinner } from '../../CenteredSpinner/CenteredSpinner';
import { PresetItem } from '../PresetItem/PresetItem';
import { useDebounce } from '../../../hooks/useDebounce';
import { useSearchAssets } from '../../../hooks/useSearchAssets';
import { useAssetById } from '../../../hooks/useAssetById';
import { FormField } from '../../Form/shared/FormField/FormField';
import { getFirstUnselectedResult } from '../helpers';

import type { SvpAsset } from '../../../types';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import styles from './AssetsSearch.module.scss';

type Props = {
  selectedAssets: SvpAsset[];
  onAddClick: (asset: SvpAsset) => void;
  onRemoveClick?: (asset: SvpAsset) => void;
  label?: string;
};

export const AssetsSearch: FC<Props> = ({
  selectedAssets = [],
  onAddClick,
  onRemoveClick,
  label = 'Search by title or ID',
}) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const numericId = /^\d+$/.test(debouncedQuery)
    ? Number(debouncedQuery)
    : undefined;

  const { data: assets = [], isFetching } = useSearchAssets(
    debouncedQuery ? { query: debouncedQuery } : undefined,
    { enabled: Boolean(debouncedQuery) }
  );
  const { data: assetById } = useAssetById(numericId);

  const results = useMemo(() => {
    if (!assetById || assets.some(({ id }) => id === assetById.id)) {
      return assets;
    }

    return [assetById, ...assets];
  }, [assets, assetById]);

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    const selectedIds = selectedAssets.map(({ id }) => id);
    const nextResult = getFirstUnselectedResult(results, selectedIds);

    if (nextResult) {
      onAddClick(nextResult);
      setQuery('');
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.search}>
        <FormField label={label}>
          <Input
            data-testid="preset-search-input"
            value={query}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              setQuery(value);
            }}
            onKeyDown={onInputKeyDown}
          />
        </FormField>
        <small className={styles.hint}>Press Enter to add the top result</small>
        {isFetching && <CenteredSpinner />}
        <ul className={styles.assetsList} data-testid="preset-results">
          {results.map((asset) => {
            const isSelected = selectedAssets
              .map((presetItem) => presetItem.id)
              .includes(asset.id);

            return (
              <li key={asset.id}>
                <PresetItem
                  item={asset}
                  dataType={'assets'}
                  isSelected={isSelected}
                  onClick={() =>
                    isSelected ? onRemoveClick?.(asset) : onAddClick(asset)
                  }
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
